/**
 * The piles the family's lists are sorted into.
 *
 * `lib/grouped` remembers a sorted list *per function it was sorted by*, so the
 * functions live here, once, rather than being written out wherever they are
 * wanted: two screens that ask "which recordings are on this story?" through
 * the same `contributionStory` share one sorting of the list, and two that each
 * wrote their own arrow would each pay for it again.
 *
 * Everything here reads one field and changes nothing. What may be sorted by on
 * which list is a rule of `lib/grouped` (see the note there): the lists AppState
 * hands out are replaced whenever anything changes, so any of these is safe on
 * them; on the mock's own long-lived tables only the ones marked *never
 * changes* are.
 */
import type { FamilyPhoto, PersonPlace, PersonPortrait, Story, StoryContribution, StoryPerson } from './types';

/** Whoever told the story. */
export const storyTeller = (s: Story) => s.primaryStorytellerPersonId;

/** The story a recording is part of. */
export const contributionStory = (c: StoryContribution) => c.storyId;
/** Whose voice a recording is. */
export const contributionSpeaker = (c: StoryContribution) => c.speakerPersonId;

/** Who a story names (its subjects, the people who took part, the people mentioned). */
export const storyPersonPerson = (sp: StoryPerson) => sp.personId;
/** And the other way round: the story a naming belongs to. */
export const storyPersonStory = (sp: StoryPerson) => sp.storyId;

/** Whose circle a face was chosen for. */
export const portraitPerson = (p: PersonPortrait) => p.personId;

/** What a Moment was added to — a day, a story, a birth, a recipe — as `kind:id`. *Never changes* once the Moment is made. */
export const momentAnchor = (m: FamilyPhoto) => (m.addedTo ? `${m.addedTo.kind}:${m.addedTo.id}` : null);
/** Everybody pinned to a Moment (for `groupedByEach`: one picture, several piles). */
export const momentPeople = (m: FamilyPhoto) => m.personIds;
/** The account that shared it. *Never changes.* */
export const momentUploader = (m: FamilyPhoto) => m.uploadedByAccountId;

/** Whose record a place row is part of. *Never changes*: a home or a visit does not change hands. */
export const placePerson = (p: PersonPlace) => p.personId;
